import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { TankColumn, ComplianceTable } from "../components/acms/Shared";
import { Badge } from "../components/ui/badge";
import { Droplets, TrendingUp, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea, CartesianGrid, Legend } from "recharts";
import { capacityLitres, liveTank, tankLevelPct } from "../lib/tankLevel";

// Level, litres and signal status: the one shared calculation (lib/tankLevel.js, mirror of
// backend/tank_level.py — /assets/live-values returns the backend's result as live.tank).

// /assets/live-values returns each register's last known value with its real last-seen time,
// even when the device behind it has gone quiet — same stale rule as Live Monitor.
const STALE_AFTER_MS = 5 * 60 * 1000;

function fmtAge(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
}

// Compact card — kept short so every tank fits on screen without scrolling. A stale reading
// gets an amber ring and a "Stale · Xh ago" pill on the card edge.
function Card({ title, subtitle, accent, live, footer, children, testid }) {
  const stale = live?.ingested_at && Date.now() - new Date(live.ingested_at).getTime() > STALE_AFTER_MS;
  return (
    <div className={`glass p-3 rounded-lg flex flex-col min-w-0 relative ${stale ? "ring-2 ring-amber-300" : ""}`} data-testid={testid}>
      {stale && (
        <span
          className="absolute -top-2 right-2 z-10 inline-flex items-center gap-1 px-1.5 py-px rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[9px] font-bold uppercase tracking-wider"
          title={`Last reading ${new Date(live.ingested_at).toLocaleString("en-IN")} — no newer data from this device`}
        >
          <AlertTriangle className="w-3 h-3" strokeWidth={2.5} /> Stale · {fmtAge(live.ingested_at)}
        </span>
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <h3 className="text-xs uppercase tracking-wider text-gray-600 font-bold truncate" title={title}>{title}</h3>
          {subtitle && <p className="text-[10px] text-gray-500 truncate">{subtitle}</p>}
        </div>
        {accent && <Badge className={`border-0 shrink-0 ${accent === "OK" ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : accent === "WARNING" ? "bg-amber-50 text-amber-700" : "bg-red-50 border border-red-200 text-red-700"}`}>{accent}</Badge>}
      </div>
      <div className="flex-1">{children}</div>
      {footer}
    </div>
  );
}

function Stat({ label, children, className = "" }) {
  return (
    <div className="bg-gray-50 rounded px-2 py-1 min-w-0">
      <div className="text-gray-500 uppercase text-[9px] tracking-wider">{label}</div>
      <div className={`font-semibold text-[11px] truncate ${className}`}>{children}</div>
    </div>
  );
}

const statusLabel = (pct) => {
  // GR threshold: ≥ 50% COMPLIANT, 30-50% WARNING, <30% CRITICAL
  if (pct >= 75) return "OK";
  if (pct >= 50) return "OK";
  if (pct >= 30) return "WARNING";
  return "CRITICAL";
};

function LevelHistoryChart({ selectedSite, tank }) {
  const [hist, setHist] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!selectedSite || !tank) return;
    let cancelled = false;
    const load = () => {
      api.get(`/v1/sites/${selectedSite}/telemetry/parameter-history`, { params: { parameter: tank.io_point_name, hours: 720 } })
        .then((r) => {
          if (cancelled) return;
          // parameter-history returns the raw register value (raw mA), not the 0-100 percent this
          // chart's Y-axis expects — convert the same way as the live level, or the line plots
          // mA values (4-20) on a 0-100 axis and shows a near-flat sliver near 0.
          setHist((r.data || []).map((d) => ({ ...d, value: tankLevelPct(tank.meta, Number(d.value)), day: new Date(d.ingested_at || d.timestamp).toLocaleDateString([], { month: "short", day: "numeric" }) })));
          setLoaded(true);
        }).catch(() => { if (!cancelled) setLoaded(true); });
    };
    load();
    const i = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(i); };
  }, [selectedSite, tank]);

  if (!loaded) return <div className="h-[300px] flex items-center justify-center text-xs text-gray-400">Loading…</div>;
  if (!hist.length) return <div className="h-[300px] flex items-center justify-center text-xs text-gray-400">No level history yet.</div>;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={hist} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid stroke="rgba(0,0,0,0.05)" strokeDasharray="3 3" />
        <XAxis dataKey="day" stroke="#94a3b8" fontSize={10} minTickGap={40} />
        <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={10} width={36} />
        <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#1f2937" }} formatter={(v) => [`${Number(v).toFixed(1)}%`, ""]} />
        <Legend />
        <ReferenceArea y1={0} y2={50} fill="#ef4444" fillOpacity={0.06} />
        <ReferenceLine y={50} stroke="#ff3366" strokeDasharray="5 5" label={{ value: "Critical 50%", position: "right", fill: "#ff3366", fontSize: 10 }} />
        <Line type="monotone" dataKey="value" name="Level %" stroke="#4fd1ff" strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function TankPage() {
  const { selectedSite, sites } = useSiteContext();
  const [tanks, setTanks] = useState([]);
  const [liveValues, setLiveValues] = useState({});
  const [trendTank, setTrendTank] = useState(null);

  // The old `/telemetry/{site}` and `/site-config-full/{site}` fetches here were never read
  // anywhere on this page — dropped rather than polled for nothing.
  useEffect(() => {
    if (!selectedSite) return;
    const load = () => {
      api.get(`/assets`, { params: { site_id: selectedSite, asset_type: "tank" } }).then((r) => setTanks(r.data)).catch(() => {});
      api.get(`/assets/live-values`, { params: { site_id: selectedSite } }).then((r) => setLiveValues(r.data || {})).catch(() => {});
    };
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [selectedSite]);

  const site = sites.find((s) => s.site_id === selectedSite);

  const complianceRows = tanks.map((tank) => {
    const live = liveValues[tank.asset_id];
    const t = live ? liveTank(tank, live) : null;
    const level = t ? t.level_pct : null;
    const minPct = Number(tank.meta?.min_level_pct || 50);
    return {
      parameter: tank.name,
      current: level !== null ? `${level.toFixed(1)}%` : t?.status === "SENSOR FAULT" ? "Sensor fault" : "—",
      required: `≥ ${minPct}%`,
      // null = no live data → shown as UNKNOWN; false = below threshold → CRITICAL
      ok: level !== null ? level >= minPct : null,
    };
  });

  return (
    <div className="space-y-3" data-testid="tank-page">
      {/* Header — one compact row */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-3 min-w-0 flex-wrap">
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">Fire Water Tank</h1>
          <span className="text-[11px] text-gray-500 truncate">
            Dashboard → {site?.name} · GR Appendix c · auto-refresh 5s
          </span>
        </div>
      </div>

      {!tanks.length ? (
        <div className="glass rounded-lg p-8 text-center text-gray-500 text-sm" data-testid="no-tanks-message">
          No tanks registered yet. <a href="/registry/tanks" className="text-orange-600 hover:text-orange-700 font-semibold">Add tanks in Registry</a>
        </div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }} data-testid="registered-tanks-section">
          {tanks.map((tank) => {
            // meta.capacity_kl (litres = capacity_kl * 1000) is the real, Asset Registry-configured
            // capacity — meta.capacity_litres doesn't exist in the registry schema at all, so
            // reading it always fell through to a hardcoded 25,000 regardless of the tank's
            // actual configured size. No fallback here: an unconfigured tank shows "—" instead
            // of a fake capacity, same as Live Monitor's tank card.
            // Capacity Override (KL) if set, else the Computed Capacity from the tank's dimensions.
            const live = liveValues[tank.asset_id];
            const t = live ? liveTank(tank, live) : null;
            const capacity = t?.capacity_l ?? capacityLitres(tank.meta);
            // null when there's no reading, or the signal is faulty / the setup incomplete — a fault
            // must never be drawn as an empty tank (client spec §5).
            const level = t ? t.level_pct : null;
            const hasTrend = tank.gateway_id && tank.io_point_name;
            return (
              <Card
                key={tank.asset_id}
                title={tank.name}
                subtitle={tank.location_label || tank.location}
                accent={level !== null ? statusLabel(level) : undefined}
                live={live}
                testid={`card-tank-${tank.asset_id}`}
                footer={hasTrend && (
                  <button
                    type="button"
                    onClick={() => setTrendTank(tank)}
                    className="mt-2 self-center inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-gray-200 text-[10px] font-semibold uppercase tracking-wider text-gray-500 hover:text-sky-600 hover:border-sky-300 hover:bg-sky-50 transition-colors"
                  >
                    <TrendingUp className="w-3 h-3" strokeWidth={2.5} /> 30-day history
                  </button>
                )}
              >
                <div className="flex items-center gap-3">
                  {level !== null ? (
                    // TankColumn draws at a fixed 180px; shrink it here only (its viewBox keeps
                    // the aspect ratio) — other pages share the component at full size.
                    <div className="shrink-0 [&_svg]:h-[120px] [&_svg]:w-auto">
                      <TankColumn value={level} capacityLitres={capacity} />
                    </div>
                  ) : (
                    <div className="shrink-0 w-24 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                        t?.status === "SENSOR FAULT" ? "bg-red-50 border-red-200 text-red-700" : "bg-slate-100 border-slate-200 text-slate-600"}`}>
                        {t?.status === "SENSOR FAULT" ? "Sensor fault" : t?.status === "CONFIG ERROR" ? "Setup needed"
                          : tank.gateway_id ? "Awaiting data" : "Not linked"}
                      </span>
                      {t?.status === "SENSOR FAULT" && <div className="text-[10px] text-red-600 mt-1">Level unknown — not an empty tank</div>}
                      {t?.status === "CONFIG ERROR" && <div className="text-[10px] text-slate-500 mt-1">{t.config_error}</div>}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-1 flex-1 min-w-0">
                    <Stat label="Tank Type">{tank.meta?.tank_type || tank.system || "Standard"}</Stat>
                    <Stat label="Capacity">{capacity ? `${capacity.toLocaleString()} L` : "Not configured"}</Stat>
                    <Stat label="Status" className={tank.status === "active" ? "text-emerald-700" : "text-amber-700"}>{tank.status?.toUpperCase() || "ACTIVE"}</Stat>
                    {tank.last_tested && <Stat label="Last Tested">{tank.last_tested.slice(0, 10)}</Stat>}
                  </div>
                </div>
                {t && t.signal != null && (
                  <div className="mt-1.5 text-[10px] text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5" data-testid={`tank-signal-${tank.asset_id}`}>
                    <span>Signal <b className="text-gray-700">{Number(t.signal).toFixed(2)} {t.unit}</b></span>
                    {t.depth_m != null && <span>Depth <b className="text-gray-700">{t.depth_m.toFixed(2)} m</b></span>}
                    {(t.status === "UNDER-RANGE" || t.status === "OVER-RANGE") && (
                      <span className="text-amber-700 font-semibold">{t.status === "UNDER-RANGE" ? "Under-range — shown as empty" : "Over-range — shown as full"}</span>
                    )}
                    {/* Hidden for now — range_note is still computed in lib/tankLevel.js */}
                    {/* {t.range_note && <span className="text-amber-700">⚠ {t.range_note}</span>} */}
                  </div>
                )}
                {/* Low/High/Critical-Low Level Detection — computed from the tank's existing
                    live level % against its own existing threshold fields, not a new register
                    link. Each only shown if that threshold was actually set, i.e. "activated"
                    individually per tank. */}
                {level !== null && (tank.meta?.level_low_pct || tank.meta?.level_high_pct || tank.meta?.level_low_low_pct) && (
                  <div className="grid grid-cols-3 gap-1 mt-2">
                    {tank.meta?.level_low_low_pct && (
                      <Stat label="Critical Low" className={level <= Number(tank.meta.level_low_low_pct) ? "text-red-600" : "text-emerald-600"}>{level <= Number(tank.meta.level_low_low_pct) ? "DETECTED" : "Normal"}</Stat>
                    )}
                    {tank.meta?.level_low_pct && (
                      <Stat label="Low Level" className={level <= Number(tank.meta.level_low_pct) ? "text-amber-600" : "text-emerald-600"}>{level <= Number(tank.meta.level_low_pct) ? "DETECTED" : "Normal"}</Stat>
                    )}
                    {tank.meta?.level_high_pct && (
                      <Stat label="High Level" className={level >= Number(tank.meta.level_high_pct) ? "text-amber-600" : "text-emerald-600"}>{level >= Number(tank.meta.level_high_pct) ? "DETECTED" : "Normal"}</Stat>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* 30-day level history — per tank, in a modal so it doesn't push the cards off screen */}
      <Dialog open={!!trendTank} onOpenChange={(open) => { if (!open) setTrendTank(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Droplets className="w-4 h-4 text-sky-500" strokeWidth={2.5} /> {trendTank?.name} — Level History (Last 30 Days)
            </DialogTitle>
          </DialogHeader>
          {trendTank && <LevelHistoryChart selectedSite={selectedSite} tank={trendTank} />}
        </DialogContent>
      </Dialog>

      {/* GR compliance — mandatory, so always shown inline below the cards (not behind a modal) */}
      {tanks.length > 0 && (
        <ComplianceTable title="Section B — Fire Water Tanks (GR Appendix c)" rows={complianceRows} />
      )}
    </div>
  );
}
